import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { StatusChip } from "./MarketingOverview";
import { PencilSimple, Copy, Pause, Play, Archive, X,
         DesktopTower, DeviceMobile, Robot, EnvelopeOpen,
         CursorClick, Globe } from "@phosphor-icons/react";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const SOURCE = {
  csv_import: { fg: "#0055FF", label: "CSV Lists" },
  client: { fg: "#16A34A", label: "Clients" },
  crm_lead: { fg: "#7C3AED", label: "Leads" },
};

export default function CampaignReportPage() {
  const { campaignId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  // Drilldown modal — set to a status name to open ("sent","delivered",
  // "opened","clicked","bounced","unsubscribed","bot_clicks"), null when closed.
  const [drillStatus, setDrillStatus] = useState(null);
  // Recipient activity feed state.
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showBots, setShowBots] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get(`/marketing/campaigns/${campaignId}/report`);
      setReport(r.data);
    } catch {
      toast.error("Failed to load report");
    }
    setLoading(false);
  };
  useEffect(() => { reload(); }, [campaignId]);

  // Load the chronological recipient-activity feed alongside the report.
  // Re-fetches when the bot-toggle changes so operators can see what
  // the scanner traffic looked like.
  useEffect(() => {
    if (!campaignId) return;
    setActivityLoading(true);
    api.get(`/marketing/campaigns/${campaignId}/activity?limit=300&include_bots=${showBots ? 1 : 0}`)
      .then(r => setActivity(r.data))
      .catch(() => setActivity({ items: [], include_bots: showBots }))
      .finally(() => setActivityLoading(false));
  }, [campaignId, showBots]);

  // auto-refresh while the send is in flight
  useEffect(() => {
    if (report?.campaign?.status === "sending") {
      const t = setInterval(reload, 5000);
      return () => clearInterval(t);
    }
  }, [report?.campaign?.status]); // eslint-disable-line

  if (loading || !report) {
    return <MarketingShell><p className="text-sm text-[#A3A3A3]">Loading report…</p></MarketingShell>;
  }

  const { campaign, totals, rates, link_clicks, by_source, by_device } = report;

  const doPause = async () => {
    try { await api.post(`/marketing/campaigns/${campaignId}/pause`); toast.success("Paused"); reload(); }
    catch { toast.error("Pause failed"); }
  };
  const doResume = async () => {
    try { await api.post(`/marketing/campaigns/${campaignId}/resume`); toast.success("Resumed"); reload(); }
    catch { toast.error("Resume failed"); }
  };
  const doClone = async () => {
    try {
      const r = await api.post(`/marketing/campaigns/${campaignId}/clone`);
      toast.success("Cloned"); nav(`/marketing/campaigns/${r.data.id}/edit`);
    } catch { toast.error("Clone failed"); }
  };
  const doArchive = async () => {
    if (!window.confirm("Archive this campaign?")) return;
    try { await api.delete(`/marketing/campaigns/${campaignId}`); toast.success("Archived"); nav("/marketing/campaigns"); }
    catch { toast.error("Failed"); }
  };

  return (
    <MarketingShell>
      {/* Header */}
      <div className="bg-white border border-[#E5E5E5] p-5 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Campaign report</p>
          <h1 className="text-2xl font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="report-name">
            {campaign.name}
          </h1>
          <div className="flex items-center gap-3 mt-1 text-xs text-[#525252]">
            <StatusChip status={campaign.status} />
            <span>{campaign.from_name}</span>
            {campaign.sent_at && <span>Sent {formatLocal(campaign.sent_at)}</span>}
            {!campaign.sent_at && campaign.scheduled_at && <span>Scheduled {formatLocal(campaign.scheduled_at)}</span>}
          </div>
        </div>
        {campaign.status === "draft" && (
          <button onClick={() => nav(`/marketing/campaigns/${campaignId}/edit`)} className="h-9 px-3 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="edit-btn">
            <PencilSimple size={12} />Edit
          </button>
        )}
        {["sent", "sending", "scheduled", "archived", "paused"].includes(campaign.status) && (
          <button onClick={() => nav(`/marketing/campaigns/${campaignId}/edit`)}
                  className="h-9 px-3 border border-[#E5E5E5] text-[#525252] text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:border-[#0055FF] hover:text-[#0055FF]"
                  data-testid="view-content-btn">
            <PencilSimple size={12} />View content
          </button>
        )}
        {campaign.status === "sending" && (
          <button onClick={doPause} className="h-9 px-3 border border-[#E5E5E5] hover:border-[#D97706] text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="pause-btn">
            <Pause size={12} />Pause
          </button>
        )}
        {campaign.status === "paused" && (
          <button onClick={doResume} className="h-9 px-3 border border-[#16A34A] text-[#16A34A] text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="resume-btn">
            <Play size={12} />Resume
          </button>
        )}
        <button onClick={doClone} className="h-9 px-3 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid="clone-btn">
          <Copy size={12} />Clone
        </button>
        {campaign.status !== "archived" && (
          <button onClick={doArchive} className="h-9 px-3 border border-[#E5E5E5] hover:border-[#DC2626] text-xs font-bold uppercase tracking-wider text-[#525252] flex items-center gap-1" data-testid="archive-btn">
            <Archive size={12} />Archive
          </button>
        )}
      </div>

      {/* Core metrics — all clickable to drill into the recipient list */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
        <Metric label="Sent" value={totals.sent} testid="metric-sent"
                onClick={() => setDrillStatus("sent")} />
        <Metric label="Delivered" value={totals.delivered} testid="metric-delivered"
                onClick={() => setDrillStatus("delivered")} />
        <Metric label="Unique Opens" value={totals.unique_opens} sub={fmtPct(rates.open_rate)} testid="metric-opens"
                onClick={() => setDrillStatus("opened")}
                hint={`Pixel-only: ${totals.unique_opens_pixel ?? 0} (the rest are implicit from clicks — see Notes)`} />
        <Metric label="Unique Clicks" value={totals.unique_clicks} sub={fmtPct(rates.click_rate)} testid="metric-clicks"
                onClick={() => setDrillStatus("clicked")} />
        <Metric label="Click→Open" value={fmtPct(rates.click_to_open_rate)} testid="metric-ctor"
                hint="Of recipients who opened, how many clicked. Capped at 100% — implicit opens from clicks prevent the inflated v1.33 number you saw before." />
        <Metric label="Bounced" value={totals.bounced} sub={fmtPct(rates.bounce_rate)} negative testid="metric-bounced"
                onClick={() => setDrillStatus("bounced")} />
        <Metric label="Unsubscribes" value={totals.unsubscribes} sub={fmtPct(rates.unsubscribe_rate)} negative testid="metric-unsubs"
                onClick={() => setDrillStatus("unsubscribed")} />
      </div>

      {/* Bot-click banner (only when scanner traffic was detected) */}
      {(totals.bot_clicks_total ?? 0) > 0 && (
        <div className="bg-[#FFF7ED] border border-[#FED7AA] p-3 mb-4 flex items-center gap-3" data-testid="bot-clicks-banner">
          <Robot size={16} weight="duotone" className="text-[#D97706]" />
          <div className="flex-1 text-xs text-[#7C2D12]">
            <span className="font-bold">{totals.bot_clicks_total}</span> click event{totals.bot_clicks_total === 1 ? "" : "s"} from corporate email scanners were filtered out of the click totals (Microsoft Defender / Mimecast / Proofpoint Safe-Links etc).
          </div>
          <button onClick={() => setDrillStatus("bot_clicks")}
                  className="h-7 px-3 border border-[#FED7AA] text-[10px] font-bold uppercase tracking-wider text-[#7C2D12] hover:bg-[#FFEDD5]"
                  data-testid="show-bot-clicks-btn">
            Show scanner clicks
          </button>
        </div>
      )}

      {/* Source breakdown + Link clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
        <div className="bg-white border border-[#E5E5E5] p-5" data-testid="source-breakdown">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-3">Recipients by source</h2>
          <SourceDonut by_source={by_source} />
          <table className="w-full text-sm mt-4">
            <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left font-bold py-1">Source</th>
                <th className="text-right font-bold py-1">Sent</th>
                <th className="text-right font-bold py-1">Opens</th>
                <th className="text-right font-bold py-1">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(by_source).map(([k, v]) => {
                const cfg = SOURCE[k];
                if (!cfg) return null;
                return (
                  <tr key={k} className="border-t border-[#F5F5F5]">
                    <td className="py-2 flex items-center gap-2">
                      <span className="w-2 h-2" style={{ backgroundColor: cfg.fg }} />
                      <span>{cfg.label}</span>
                    </td>
                    <td className="text-right py-2 tabular-nums">{v.sent}</td>
                    <td className="text-right py-2 tabular-nums">{v.opened}</td>
                    <td className="text-right py-2 tabular-nums">{v.clicked}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-5 py-3 border-b border-[#E5E5E5]">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Link-level clicks</h2>
          </div>
          {link_clicks.length === 0 ? (
            <p className="p-6 text-sm text-[#A3A3A3]">No clicks recorded yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                <tr>
                  <th className="text-left px-5 py-2 font-bold">URL</th>
                  <th className="text-right px-3 py-2 font-bold">Total</th>
                  <th className="text-right px-5 py-2 font-bold">Unique</th>
                </tr>
              </thead>
              <tbody>
                {link_clicks.map((l, i) => (
                  <tr key={i} className="border-t border-[#F5F5F5]">
                    <td className="px-5 py-2 text-xs text-[#525252] truncate max-w-md">{l.url}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{l.clicks}</td>
                    <td className="px-5 py-2 text-right tabular-nums font-semibold">{l.unique_clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recipient activity feed — chronological log of every open + click */}
      <RecipientActivityPanel
        activity={activity}
        loading={activityLoading}
        showBots={showBots}
        onToggleBots={() => setShowBots(b => !b)}
      />

      {/* Device breakdown */}
      {by_device && (
        <div className="bg-white border border-[#E5E5E5] p-5 mt-4" data-testid="device-breakdown">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-3">Opens by device</h2>
          <DeviceBars data={by_device} />
        </div>
      )}
      {/* Drilldown modal — pops when a metric card is clicked */}
      {drillStatus && (
        <RecipientDrilldownModal
          campaignId={campaignId}
          status={drillStatus}
          onClose={() => setDrillStatus(null)}
        />
      )}

    </MarketingShell>
  );
}

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  // `v` is a fraction (0.78) — but the new `click_to_open_rate` card
  // already passes a fraction too, so the same formatter works.
  if (typeof v === "string") return v;
  return `${(v * 100).toFixed(1)}%`;
}

function Metric({ label, value, sub, negative, testid, onClick, hint }) {
  const interactive = typeof onClick === "function";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={`bg-white border border-[#E5E5E5] p-4 text-left w-full transition-colors ${
        interactive ? "hover:border-[#0055FF] cursor-pointer focus:outline-none focus:border-[#0055FF]" : "cursor-default"
      }`}
      title={hint || (interactive ? "Click to see the recipient list" : "")}
      data-testid={testid}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">{label}</p>
      <p className={`text-2xl font-black tabular-nums mt-0.5 ${negative && value ? "text-[#DC2626]" : "text-[#0A0A0A]"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
        {value ?? 0}
      </p>
      {sub && <p className="text-xs text-[#525252] mt-0.5">{sub}</p>}
    </button>
  );
}

function SourceDonut({ by_source }) {  const entries = Object.entries(by_source).filter(([, v]) => v.sent > 0);
  const total = entries.reduce((a, [, v]) => a + v.sent, 0);
  if (total === 0) {
    return <div className="h-32 flex items-center justify-center text-xs text-[#A3A3A3]">No recipients yet</div>;
  }
  const size = 140, stroke = 22, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  let cursor = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto block">
      {entries.map(([k, v]) => {
        const pct = v.sent / total;
        const dasharray = `${pct * c} ${c}`;
        const rotate = (cursor / total) * 360 - 90;
        cursor += v.sent;
        const color = SOURCE[k]?.fg || "#A3A3A3";
        return (
          <circle key={k} cx={size / 2} cy={size / 2} r={r}
                  fill="none" stroke={color} strokeWidth={stroke}
                  strokeDasharray={dasharray}
                  transform={`rotate(${rotate} ${size / 2} ${size / 2})`} />
        );
      })}
      <text x={size / 2} y={size / 2 + 4} textAnchor="middle" className="fill-[#0A0A0A]"
            style={{ fontSize: 18, fontWeight: 900, fontFamily: "Cabinet Grotesk, sans-serif" }}>
        {total}
      </text>
    </svg>
  );
}

const DEVICE_CFG = {
  mobile:  { label: "Mobile",  fg: "#0055FF" },
  desktop: { label: "Desktop", fg: "#16A34A" },
  tablet:  { label: "Tablet",  fg: "#D97706" },
  bot:     { label: "Bots",    fg: "#A3A3A3" },
  unknown: { label: "Unknown", fg: "#525252" },
};

function DeviceBars({ data }) {
  const entries = Object.entries(data).filter(([, v]) => v > 0);
  const total = entries.reduce((a, [, v]) => a + v, 0);
  if (total === 0) {
    return <p className="text-xs text-[#A3A3A3]">No open events with device info yet.</p>;
  }
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => {
        const cfg = DEVICE_CFG[k] || { label: k, fg: "#A3A3A3" };
        const pct = total > 0 ? (v / total) * 100 : 0;
        return (
          <div key={k} data-testid={`device-row-${k}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-semibold text-[#0A0A0A]">{cfg.label}</span>
              <span className="tabular-nums text-[#525252]">{v} <span className="text-[#A3A3A3]">({pct.toFixed(0)}%)</span></span>
            </div>
            <div className="h-2 bg-[#F5F5F5] overflow-hidden">
              <div className="h-full" style={{ width: `${pct}%`, backgroundColor: cfg.fg }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Recipient drill-down modal (clicked from a metric card)
// ──────────────────────────────────────────────────────────────────────────
const DRILL_TITLE = {
  sent:         { label: "Sent",                 hint: "Every recipient this campaign delivered to (including bounces and dropped)" },
  delivered:    { label: "Delivered",            hint: "Recipients SendGrid confirmed reached the inbox / spam folder" },
  bounced:      { label: "Bounced",              hint: "Recipients whose mail server rejected the message" },
  failed:       { label: "Failed",               hint: "Sends that never left our queue (validation errors, dropped, blocked, spam)" },
  opened:       { label: "Opened",               hint: "Recipients who triggered an open event — pixel OR implicit-from-click" },
  clicked:      { label: "Clicked",              hint: "Humans who clicked a link (bot scanners excluded)" },
  unsubscribed: { label: "Unsubscribed",         hint: "Recipients who unsubscribed via the footer link" },
  complained:   { label: "Spam complaints",      hint: "Recipients who clicked 'Report spam' in their mail client" },
  bot_clicks:   { label: "Scanner clicks (bots)", hint: "Corporate email-security scanners that clicked links during URL inspection" },
};

function RecipientDrilldownModal({ campaignId, status, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/marketing/campaigns/${campaignId}/recipients?status=${status}&limit=500`)
      .then(r => setRows(r.data))
      .catch(() => { setRows([]); toast.error("Failed to load recipient list"); })
      .finally(() => setLoading(false));
  }, [campaignId, status]);

  const title = DRILL_TITLE[status] || { label: status, hint: "" };
  const exportCsv = () => {
    if (!rows || rows.length === 0) return;
    const header = ["email", "full_name", "company_name", "status", "sent_at", "last_event_at", "event_count", "top_url"];
    const lines = [header.join(",")];
    for (const r of rows) {
      lines.push(header.map(h => {
        const v = r[h] ?? "";
        const s = String(v).replace(/"/g, '""');
        return /[,"\n]/.test(s) ? `"${s}"` : s;
      }).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${campaignId}-${status}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="drilldown-backdrop">
      <div className="bg-white w-full max-w-4xl max-h-[80vh] flex flex-col"
           onClick={e => e.stopPropagation()} data-testid="drilldown-modal">
        <div className="p-5 border-b border-[#E5E5E5] flex items-start gap-3">
          <div className="flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Recipient drilldown</p>
            <h2 className="text-xl font-black text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {title.label}{rows ? ` · ${rows.length}` : ""}
            </h2>
            <p className="text-xs text-[#525252] mt-0.5">{title.hint}</p>
          </div>
          <button onClick={exportCsv}
                  disabled={!rows || rows.length === 0}
                  className="h-8 px-3 border border-[#E5E5E5] text-xs font-bold uppercase tracking-wider hover:border-[#0055FF] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="drilldown-export-csv">
            Export CSV
          </button>
          <button onClick={onClose}
                  className="h-8 w-8 flex items-center justify-center border border-[#E5E5E5] hover:border-[#DC2626]"
                  data-testid="drilldown-close">
            <X size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading && <p className="p-5 text-sm text-[#A3A3A3]">Loading…</p>}
          {!loading && rows && rows.length === 0 && (
            <p className="p-5 text-sm text-[#A3A3A3]">No recipients match {title.label.toLowerCase()}.</p>
          )}
          {!loading && rows && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3] sticky top-0 bg-white">
                <tr>
                  <th className="text-left px-5 py-2 font-bold">Email</th>
                  <th className="text-left px-3 py-2 font-bold">Name</th>
                  <th className="text-left px-3 py-2 font-bold">Company</th>
                  <th className="text-left px-3 py-2 font-bold">Time</th>
                  {status === "clicked" || status === "bot_clicks"
                    ? <th className="text-left px-3 py-2 font-bold">Last URL</th>
                    : null}
                  {status === "bot_clicks"
                    ? <th className="text-left px-3 py-2 font-bold">Reason</th>
                    : null}
                  <th className="text-right px-5 py-2 font-bold">Events</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`drilldown-row-${i}`}>
                    <td className="px-5 py-2 font-medium text-[#0A0A0A]">{r.email}</td>
                    <td className="px-3 py-2 text-[#525252]">{r.full_name || "—"}</td>
                    <td className="px-3 py-2 text-[#525252]">{r.company_name || "—"}</td>
                    <td className="px-3 py-2 text-[#525252] tabular-nums whitespace-nowrap">
                      {r.last_event_at ? formatLocal(r.last_event_at) : (r.sent_at ? formatLocalDate(r.sent_at) : "—")}
                    </td>
                    {status === "clicked" || status === "bot_clicks" ? (
                      <td className="px-3 py-2 text-[10px] text-[#525252] truncate max-w-xs">{r.top_url || "—"}</td>
                    ) : null}
                    {status === "bot_clicks" ? (
                      <td className="px-3 py-2 text-[10px] text-[#7C2D12]">{r.bot_reason || "—"}</td>
                    ) : null}
                    <td className="px-5 py-2 text-right tabular-nums">{r.event_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────────
// Recipient Activity panel — chronological feed of opens + clicks
// ──────────────────────────────────────────────────────────────────────────
function RecipientActivityPanel({ activity, loading, showBots, onToggleBots }) {
  const items = activity?.items || [];
  return (
    <div className="bg-white border border-[#E5E5E5] mt-4" data-testid="activity-panel">
      <div className="p-4 border-b border-[#F5F5F5] flex items-center gap-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] flex-1">Recipient activity</h2>
        <label className="flex items-center gap-1.5 text-[11px] text-[#525252] cursor-pointer" data-testid="activity-show-bots">
          <input type="checkbox" checked={showBots} onChange={onToggleBots} className="w-3.5 h-3.5" />
          Include scanner clicks
        </label>
      </div>
      <div className="max-h-[420px] overflow-auto">
        {loading && <p className="p-5 text-sm text-[#A3A3A3]">Loading activity…</p>}
        {!loading && items.length === 0 && (
          <p className="p-5 text-sm text-[#A3A3A3]">No engagement events recorded yet.</p>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[#A3A3A3] sticky top-0 bg-white">
              <tr>
                <th className="text-left px-4 py-2 font-bold w-[110px]">When</th>
                <th className="text-left px-2 py-2 font-bold w-[120px]">Event</th>
                <th className="text-left px-2 py-2 font-bold">Recipient</th>
                <th className="text-left px-2 py-2 font-bold">URL</th>
                <th className="text-left px-2 py-2 font-bold w-[40px]">Device</th>
              </tr>
            </thead>
            <tbody>
              {items.map((ev) => (
                <tr key={ev.id} className="border-t border-[#F5F5F5]" data-testid={`activity-row-${ev.id}`}>
                  <td className="px-4 py-2 text-[#525252] tabular-nums whitespace-nowrap text-[11px]">
                    {ev.occurred_at ? formatLocal(ev.occurred_at) : "—"}
                  </td>
                  <td className="px-2 py-2">
                    <ActivityChip type={ev.event_type} isBot={ev.is_bot} reason={ev.bot_reason} />
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-medium text-[#0A0A0A] truncate max-w-[260px]">{ev.full_name || ev.email}</div>
                    {ev.company_name && (
                      <div className="text-[10px] text-[#A3A3A3] truncate max-w-[260px]">{ev.company_name}</div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-[11px] text-[#525252] truncate max-w-[280px]">
                    {ev.url || (ev.event_type === "opened" ? "—" : "")}
                  </td>
                  <td className="px-2 py-2 text-[#525252]">
                    {ev.device === "mobile"   && <DeviceMobile size={14} />}
                    {ev.device === "desktop"  && <DesktopTower size={14} />}
                    {ev.device === "tablet"   && <DeviceMobile size={14} />}
                    {ev.device === "bot"      && <Robot size={14} />}
                    {ev.device === "unknown"  && <Globe size={14} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ActivityChip({ type, isBot, reason }) {
  if (isBot) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#7C2D12] bg-[#FFF7ED] border border-[#FED7AA]"
            title={reason ? `Bot — ${reason}` : "Bot"}>
        <Robot size={10} />Bot {type}
      </span>
    );
  }
  if (type === "opened") return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#16A34A] bg-[#F0FDF4] border border-[#BBF7D0]"><EnvelopeOpen size={10} />Opened</span>;
  if (type === "clicked") return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0055FF] bg-[#EFF6FF] border border-[#BFDBFE]"><CursorClick size={10} />Clicked</span>;
  if (type === "unsubscribed") return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA]">Unsub</span>;
  if (type === "complained") return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA]">Spam</span>;
  return <span className="text-[10px] uppercase text-[#525252]">{type}</span>;
}

